import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import ProductInfoDetailPage from 'pages/vending/ProductInfoDetailPage';

import { TRANSLATIONS as t } from 'constants/translations';

import styles from './VendingMachinePage.module.css';
import Api from 'utils/api';

const LANG_OPTIONS = [
  { code: 'Ko', label: '한국어' },
  { code: 'En', label: 'English' },
  { code: 'Ja', label: '日本語' },
  { code: 'Zh', label: '中文(简体)' },
];


const VendingMachinePage = () => {

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get('SN');

  const [lang, setLang] = useState(() => {
    return localStorage.getItem('vending_lang') || 'Ko';
  });

  useEffect(() => {
    localStorage.setItem('vending_lang', lang);
  }, [lang]);

  const [vendingInfo, setVendingInfo] = useState(null);
  const [productList, setProductList] = useState([]);
  const [activeTab, setActiveTab] = useState('전체');
  const [isDetailView, setIsDetailView] = useState(false);

  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('vending_cart');
      if (!savedCart) return [];
      const parsed = JSON.parse(savedCart);
      return parsed.filter(item => item && item.id && item.productNames);
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('vending_cart', JSON.stringify(cart));
  }, [cart]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [tempCount, setTempCount] = useState(1);
  const [isNiceAuthProcessing, setIsNiceAuthProcessing] = useState(false);

  useEffect(() => {
    if (!isLangOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.${styles.langSelectWrapper}`)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLangOpen]);

  const categories = useMemo(() => {
    if (!productList || productList.length === 0) return [];
    const uniqueTypes = [];
    const map = new Map();
    productList.forEach(item => {
      if (item.productTypeNameKo && !map.has(item.productTypeNameKo)) {
        map.set(item.productTypeNameKo, true);
        uniqueTypes.push({
          id: item.productTypeNameKo,
          Ko: item.productTypeNameKo,
          En: item.productTypeNameEn || item.productTypeNameKo,
          Ja: item.productTypeNameJa || item.productTypeNameKo,
          Zh: item.productTypeNameZh || item.productTypeNameKo,
        });
      }
    });
    return uniqueTypes;
  }, [productList]);

  const filteredProducts = useMemo(() => {
    if (activeTab === '전체') return productList;
    return productList.filter(product => product.productTypeNameKo === activeTab);
  }, [activeTab, productList]);

  const fetchMenuData = useCallback(async () => {
    if (!serialNumber) return;
    try {
      const response = await Api.get(`/machine/vending/${serialNumber}`);
      if (response) {
        setVendingInfo(response.vendingMachineInfo);
        setProductList(response.vendingMachineProductList || []);
      }
    } catch (err) {
      console.error("데이터 로딩 실패", err);
    }
  }, [serialNumber]);

  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // 모바일 뒤로 가기: 상세 화면에서는 앱을 나가지 않고 목록으로 복귀
  // ※ 이 useEffect는 반드시 모든 early return 보다 위에 있어야 함 (Hooks 규칙)
  useEffect(() => {
    if (!isDetailView) return;

    // 상세 진입 시 히스토리 항목 추가
    window.history.pushState({ detailView: true }, '');

    const handlePopState = () => {
      // 뒤로 가기가 눌리면 목록으로 복귀
      setIsDetailView(false);
      setSelectedProduct(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDetailView]);

  const handleProductClick = (product) => {
    if (product.productCount > 0) {
      const cartItem = cart.find(item => item.id === product.machineColumnId);
      setTempCount(cartItem ? cartItem.count : 1);
      setSelectedProduct(product);
      setIsDetailView(true);
      window.scrollTo(0, 0);
    }
  };

  const handleBackToList = () => {
    // 상세 진입 시 추가한 히스토리 항목을 정리
    if (window.history.state?.detailView) {
      window.history.back();   // popstate 핸들러가 상태를 정리함
    } else {
      setIsDetailView(false);
      setSelectedProduct(null);
    }
  };

  const handleCall = () => {
    const phoneNumber = vendingInfo?.csNUmber;
    if (phoneNumber) window.location.href = `tel:${phoneNumber}`;
    else alert(lang === 'Ko' ? '등록된 번호가 없습니다.' : 'No support number.');
  };

  const addToCart = () => {
    const productId = selectedProduct.machineColumnId;
    setCart(prev => {
      const idx = prev.findIndex(item => item.id === productId);
      const cartItem = {
        id: productId,
        productNames: {
          Ko: selectedProduct.productNameKo,
          En: selectedProduct.productNameEn || selectedProduct.productNameKo,
          Ja: selectedProduct.productNameJa || selectedProduct.productNameKo,
          Zh: selectedProduct.productNameZh || selectedProduct.productNameKo,
        },
        price: selectedProduct.productPrice,
        count: tempCount,
        maxStock: selectedProduct.productCount,
        image: selectedProduct.productImage,
        isForAdult: selectedProduct.isForAdultFlag === 1,
      };
      if (idx > -1) {
        const newCart = [...prev];
        newCart[idx].count = tempCount;
        return newCart;
      }
      return [...prev, cartItem];
    });
    handleBackToList();
  };

  const updateCartItemCount = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const nextCount = item.count + delta;
        if (nextCount <= 0) return null;
        if (nextCount > item.maxStock) return item;
        return { ...item, count: nextCount };
      }
      return item;
    }).filter(Boolean));
  };

  const totalCartCount = cart.reduce((acc, cur) => acc + cur.count, 0);
  const totalPrice = cart.reduce((acc, cur) => acc + (cur.price * cur.count), 0);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    if (isNiceAuthProcessing) return;  // 중복 호출 방지

    // 성인 상품 포함 여부 확인
    const hasAdultProduct = cart.some(item => item.isForAdult);

    // 성인 상품이 있으면 안내 문구 표시 후 진행 (취소 시 중단)
    if (hasAdultProduct) {
      const confirmed = window.confirm(
        lang === 'Ko'
          ? '성인 인증이 필요한 상품이 포함되어 있습니다.\nPASS 인증을 진행합니다.'
          : 'This order includes age-restricted items.\nIdentity verification (PASS) is required.'
      );
      if (!confirmed) return;  // 사용자가 취소하면 결제 중단
    }

    setIsNiceAuthProcessing(true);

    if (serialNumber) localStorage.setItem('vending_serial', serialNumber);

    const orderDetailReqList = cart.map(item => ({
      machineColumnId: item.id,
      purchaseCount: item.count,
    }));

    let niceAuthUrl = null;
    let requestNo = null;

    try {
      const response = await Api.post('/sale/order', { orderDetailReqList });
      niceAuthUrl = response?.niceAuthUrl ?? null;
      requestNo = response?.requestNo ?? null;
    } catch (err) {
      console.error('주문 요청 실패', err);
      setIsNiceAuthProcessing(false);
      return;
    }

    // 성인 상품이 없거나 개발 환경이면 인증 없이 결제 페이지로
    if (!hasAdultProduct || process.env.REACT_APP_ENV === 'development') {
      navigate('/payment', { state: { cart, totalPrice, vendingInfo, lang } });
      setIsNiceAuthProcessing(false);
      return;
    }

    // 성인 상품이 있는데 서버가 인증 URL을 주지 않은 경우 (예외 상황)
    if (!niceAuthUrl) {
      setIsNiceAuthProcessing(false);
      alert(
        lang === 'Ko'
          ? '인증 URL을 받지 못했습니다. 잠시 후 다시 시도해주세요.'
          : 'Failed to get verification URL. Please try again.'
      );
      return;
    }

    // PASS(NICE) 인증 팝업 호출
    const popup = window.open(
      niceAuthUrl,
      'niceAuth',
      'width=480,height=812,top=100,menubar=no,status=no,toolbar=no,scrollbars=no'
    );

    let elapsed = 0;
    const timer = setInterval(async () => {
      elapsed += 300;

      if (elapsed > 300000) {
        clearInterval(timer);
        setIsNiceAuthProcessing(false);
        alert(lang === 'Ko' ? '인증 시간이 초과되었습니다.' : 'Authentication timed out.');
        return;
      }

      if (!popup || popup.closed) {
        clearInterval(timer);
        try {
          const result = await Api.get(`/nice/status?requestNo=${requestNo}`);
          if (result?.verified) {
            navigate('/payment', { state: { cart, totalPrice, vendingInfo, lang } });
          } else {
            const reason = result?.reason ?? (
              lang === 'Ko' ? '성인 인증이 완료되지 않았습니다.' : 'Adult verification not completed.'
            );
            alert(reason);
          }
        } catch (err) {
          console.error('인증 결과 확인 실패', err);
          alert(lang === 'Ko' ? '인증 확인 중 오류가 발생했습니다.' : 'Verification check failed.');
        } finally {
          setIsNiceAuthProcessing(false);
        }
      }
    }, 300);
  };

  // ── 조건부 렌더링 ────────────────────────────────────────────────────
  if (!vendingInfo && productList.length === 0) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (isDetailView && selectedProduct) {
    return (
      <ProductInfoDetailPage
        selectedProduct={selectedProduct}
        tempCount={tempCount}
        setTempCount={setTempCount}
        handleBackToList={handleBackToList}
        addToCart={addToCart}
        lang={lang}
      />
    );
  }

  return (
    <div className={styles.mobileContainer}>
      <header className={styles.header}>
        <div className={styles.topRow}>
          <div className={styles.logoWrapper}>
            <img src="/mubiz_logo_2.png" alt="Logo" className={styles.logoImg} />
          </div>

          <div className={styles.langSelectWrapper}>
            <button
              type="button"
              className={styles.langTrigger}
              onClick={() => setIsLangOpen(prev => !prev)}
            >
              <svg className={styles.langIcon} viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
                {/* 연한 회색 둥근 사각형 + 진한 文 (왼쪽 위) */}
                <rect x="0" y="2" width="23" height="23" rx="7" fill="#e8e8e8" />
                <text x="11.5" y="19.5"
                  textAnchor="middle"
                  fontSize="15"
                  fontWeight="700"
                  fontFamily="'Pretendard', sans-serif"
                  fill="#333333">文</text>
                {/* 진한 회색 둥근 사각형 + 연한 A (오른쪽 살짝 아래) */}
                <rect x="14" y="13" width="20" height="20" rx="6" fill="#5f6368" />
                <text x="24" y="28"
                  textAnchor="middle"
                  fontSize="14"
                  fontWeight="700"
                  fontFamily="'Pretendard', sans-serif"
                  fill="#ffffff">A</text>
              </svg>
              <span className={styles.langTriggerText}>
                {LANG_OPTIONS.find(o => o.code === lang)?.label}
              </span>
            </button>

            {isLangOpen && (
              <ul className={styles.langDropdown}>
                {LANG_OPTIONS.map(opt => (
                  <li key={opt.code}>
                    <button
                      type="button"
                      className={`${styles.langOption} ${lang === opt.code ? styles.langOptionActive : ''}`}
                      onClick={() => { setLang(opt.code); setIsLangOpen(false); }}
                    >
                      <span className={styles.langCheck}>
                        {lang === opt.code && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </span>
                      <span className={styles.langOptionText}>{opt.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>


        </div>
        <div className={styles.storeName}>{vendingInfo?.machineName || 'Mubiz'}</div>
        <div className={styles.quickMenu}>
          <button className={styles.quickBtn} onClick={handleCall}>
            <span>🎧</span> {t.support[lang]}
          </button>
          <button className={styles.quickBtn} onClick={() => setIsCartOpen(true)}>
            <span>🛒</span> {t.cart[lang]}
            {totalCartCount > 0 && <span className={styles.cartBadge}>{totalCartCount}</span>}
          </button>
        </div>
      </header>

      <nav className={styles.categoryNav}>
        <button className={activeTab === '전체' ? styles.activeTab : ''} onClick={() => setActiveTab('전체')}>
          {t.all[lang]}
        </button>
        {categories.map(cat => (
          <button key={cat.id} className={activeTab === cat.id ? styles.activeTab : ''} onClick={() => setActiveTab(cat.id)}>
            {cat[lang]}
          </button>
        ))}
      </nav>

      <main className={styles.menuList}>
        {filteredProducts.map((product) => {
          const cartItem = cart.find(item => item.id === product.machineColumnId);
          return (
            <div key={product.machineColumnId} className={styles.menuItem} onClick={() => handleProductClick(product)}>
              <div className={styles.menuDetail}>
                <h3 className={styles.productName}>
                  {product[`productName${lang}`] || product.productNameKo}
                  {product.isForAdultFlag === 1 && <span className={styles.adultBadge}>19</span>}
                </h3>
                <p className={styles.foodPrice}>{product.productPrice?.toLocaleString()}원</p>
              </div>
              <div className={styles.imgWrapper}>
                {product.productCount === 0 && <div className={styles.soldOutOverlay}>{t.soldOut[lang]}</div>}
                <img src={product.productImage || '/default-food.png'} alt="product" />
                {cartItem && cartItem.count > 0 && (
                  <div className={styles.imageCartCount}>
                    {cartItem.count}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {filteredProducts.length === 0 && <div className={styles.emptyMsg}>{t.noProducts[lang]}</div>}
      </main>

      {isCartOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsCartOpen(false)}>
          <div className={styles.cartSheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sheetHeader}>
              <h3>{t.cart[lang]}</h3>
              <button className={styles.closeBtn} onClick={() => setIsCartOpen(false)}>✕</button>
            </div>
            <div className={styles.cartList}>
              {cart.length === 0 ? (
                <p className={styles.emptyMsg}>{t.emptyCart[lang]}</p>
              ) : (
                cart.map(item => (
                  <div key={item.id} className={styles.cartItem}>
                    <div className={styles.itemNameWrapper}>
                      <span className={styles.itemName}>
                        {item.productNames?.[lang] || item.productNames?.Ko || ''}
                        {item.isForAdult && <span className={styles.adultBadge}>19</span>}
                      </span>
                    </div>
                    <div className={styles.itemActionsWrapper}>
                      <div className={styles.itemActions}>
                        <button onClick={() => updateCartItemCount(item.id, -1)}>-</button>
                        <span>{item.count}</span>
                        <button onClick={() => updateCartItemCount(item.id, 1)}>+</button>
                      </div>
                    </div>
                    <button className={styles.deleteBtn} onClick={() => updateCartItemCount(item.id, -item.count)}>
                      <svg className={styles.trashSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                    <div className={styles.itemPriceWrapper}>
                      <span className={styles.itemPrice}>{(item.price * item.count).toLocaleString()}원</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            {cart.length > 0 && (
              <div className={styles.cartFooter}>
                <div className={styles.totalRow}>
                  <span>{t.totalAmount[lang]}</span>
                  <span className={styles.totalAmount}>{totalPrice.toLocaleString()}원</span>
                </div>
                <button className={styles.orderBtn} onClick={handleCheckout} disabled={isNiceAuthProcessing}>{t.checkout[lang]}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {totalCartCount > 0 && (
        <div className={styles.floatingCartBar} onClick={isNiceAuthProcessing ? undefined : () => setIsCartOpen(true)}>
          <div className={styles.floatingInfo}>
            <span className={styles.floatingBadge}>{totalCartCount}</span>
            <span className={styles.floatingText}>{t.viewCart[lang]}</span>
          </div>
          <div className={styles.floatingPrice}>
            {totalPrice.toLocaleString()}원 {t.checkout[lang]} 〉
          </div>
        </div>
      )}

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <p className={styles.businessInfo}>{t.bizInfo[lang]}</p>
          <div className={styles.infoGrid}>
            <span>상호: {vendingInfo?.companyName || '-'}</span>
            <span>대표자: {vendingInfo?.name || '-'}</span>
            <span>주소: {vendingInfo?.address || '-'}</span>
            <span>사업자등록번호: {vendingInfo?.businessNumber || '-'}</span>
            <span>전화: {vendingInfo?.csNUmber || '-'}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VendingMachinePage;